// Shared presentational primitives for the Users & Roles section.
import { Box, Avatar, Tooltip } from "@mui/material";
import { tokens } from "../../../theme";
import { useTheme } from "@mui/material";
import { AVATAR_COLORS, MONO_FONT, getRoleMeta } from "../data/mockUsersRoles";

// Tint a hex color to a soft background (for pills)
const tint = (hex, alpha = 0.12) => {
  const h = hex.replace("#", "");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// ── Role pill ────────────────────────────────────────────────────────────────
export const RolePill = ({ roleKey, size = "sm" }) => {
  const meta = getRoleMeta(roleKey);
  const small = size === "sm";
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        fontFamily: MONO_FONT,
        fontSize: small ? "0.625rem" : "0.6875rem",
        fontWeight: 600,
        lineHeight: 1,
        color: meta.color,
        backgroundColor: tint(meta.color, 0.1),
        border: `1px solid ${tint(meta.color, 0.25)}`,
        borderRadius: "5px",
        px: 0.75,
        py: 0.35,
        whiteSpace: "nowrap",
      }}
    >
      {roleKey}
    </Box>
  );
};

// ── Generic gray pill (project scopes, etc.) ─────────────────────────────────
export const ScopePill = ({ label }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        fontFamily: MONO_FONT,
        fontSize: "0.625rem",
        fontWeight: 500,
        color: colors.grey[500],
        backgroundColor: "rgba(100,116,139,0.08)",
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: "5px",
        px: 0.75,
        py: 0.3,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Box>
  );
};

// ── Identity-source badge (Keycloak vs LDAP) ─────────────────────────────────
export const SourceBadge = ({ federated }) => {
  const color = federated ? "#d97706" : "#16a34a";
  const label = federated ? "LDAP" : "Keycloak";
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        fontFamily: MONO_FONT,
        fontSize: "0.5625rem",
        fontWeight: 600,
        color,
        backgroundColor: tint(color, 0.1),
        border: `1px solid ${tint(color, 0.25)}`,
        borderRadius: "4px",
        px: 0.6,
        py: 0.2,
        ml: 0.75,
        verticalAlign: "middle",
      }}
    >
      {label}
    </Box>
  );
};

// ── Avatar with deterministic color ──────────────────────────────────────────
export const UserAvatar = ({ user, size = 38 }) => (
  <Avatar
    sx={{
      width: size,
      height: size,
      bgcolor: AVATAR_COLORS[user.color % AVATAR_COLORS.length],
      color: "#ffffff",
      fontSize: size > 30 ? "0.8125rem" : "0.625rem",
      fontWeight: 600,
      fontFamily: MONO_FONT,
      letterSpacing: "0.02em",
      flexShrink: 0,
    }}
  >
    {user.initials}
  </Avatar>
);

// ── Stacked mini avatars (role-card footer) ──────────────────────────────────
export const AvatarStack = ({ members, max = 4 }) => {
  const shown = members.slice(0, max);
  const extra = members.length - max;
  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      {shown.map((m, i) => (
        <Tooltip key={m.id} title={m.name} arrow>
          <Avatar
            sx={{
              width: 22,
              height: 22,
              ml: i === 0 ? 0 : "-6px",
              bgcolor: AVATAR_COLORS[m.color % AVATAR_COLORS.length],
              color: "#ffffff",
              fontSize: "0.5rem",
              fontWeight: 600,
              fontFamily: MONO_FONT,
              border: "2px solid var(--fx-paper)",
            }}
          >
            {m.initials}
          </Avatar>
        </Tooltip>
      ))}
      {extra > 0 && (
        <Avatar
          sx={{
            width: 22,
            height: 22,
            ml: "-6px",
            bgcolor: "var(--fx-border)",
            color: "var(--fx-text-secondary)",
            fontSize: "0.5rem",
            fontWeight: 600,
            fontFamily: MONO_FONT,
            border: "2px solid var(--fx-paper)",
          }}
        >
          +{extra}
        </Avatar>
      )}
    </Box>
  );
};
